import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Loader2, Zap, Clock, MapPin, CheckCircle2, XCircle, AlertTriangle, Camera, Image as ImageIcon, ShieldAlert, Star } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { TAG_LABEL, type GigTag } from "@/lib/gigTags";
import { toast } from "sonner";
import { ProofUploader } from "@/components/ProofUploader";
import { RiskBadge } from "@/components/RiskBadge";
import { ReviewForm } from "@/components/ReviewForm";
import { NotificationsBell } from "@/components/NotificationsBell";

interface Gig {
  id: string; poster_id: string; worker_id: string | null;
  title: string; description: string; tag: GigTag;
  budget_cents: number; type: "gig" | "drop"; status: string;
  location: string | null; expires_at: string | null; created_at: string;
}
interface Application {
  id: string; applicant_id: string; message: string | null; status: string; created_at: string;
  applicant?: { display_name: string; cashtag: string; hustle_level: string } | null;
}
interface Proof {
  id: string; note: string | null; media_urls: string[];
  gps_lat: number | null; gps_lng: number | null; submitted_at: string;
}
interface Dispute {
  id: string; opener_role: string; reason: string; status: string;
  ai_risk: "low" | "medium" | "high" | null; ai_summary: string | null;
  worker_payout_cents: number | null; poster_refund_cents: number | null;
  created_at: string; resolved_at: string | null;
}
interface Review {
  id: string; reviewer_id: string; subject_id: string; direction: string;
  stars: number; comment: string | null; created_at: string;
}

const GigDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gig, setGig] = useState<Gig | null>(null);
  const [poster, setPoster] = useState<{ display_name: string; cashtag: string } | null>(null);
  const [apps, setApps] = useState<Application[]>([]);
  const [myApp, setMyApp] = useState<Application | null>(null);
  const [proofs, setProofs] = useState<Proof[]>([]);
  const [dispute, setDispute] = useState<Dispute | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [proofUrls, setProofUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [disputeReason, setDisputeReason] = useState("");

  const load = useCallback(async () => {
    if (!id) return;
    const { data: g } = await supabase.from("gigs").select("*").eq("id", id).maybeSingle();
    if (!g) { setLoading(false); return; }
    setGig(g as Gig);
    const { data: p } = await supabase.from("profiles").select("display_name, cashtag").eq("user_id", (g as Gig).poster_id).maybeSingle();
    setPoster(p as any);

    const [{ data: a }, { data: pr }, { data: d }, { data: rv }] = await Promise.all([
      supabase.from("gig_applications").select("*").eq("gig_id", id).order("created_at", { ascending: false }),
      supabase.from("gig_proofs").select("*").eq("gig_id", id).order("submitted_at", { ascending: false }),
      supabase.from("gig_disputes").select("*").eq("gig_id", id).order("created_at", { ascending: false }).limit(1).maybeSingle(),
      supabase.from("gig_reviews").select("*").eq("gig_id", id).order("created_at", { ascending: false }),
    ]);

    const apps = (a as Application[]) ?? [];
    if (apps.length) {
      const ids = Array.from(new Set(apps.map((x) => x.applicant_id)));
      const { data: profs } = await supabase.from("profiles").select("user_id, display_name, cashtag, hustle_level").in("user_id", ids);
      const map = new Map((profs ?? []).map((p: any) => [p.user_id, p]));
      apps.forEach((x) => (x.applicant = map.get(x.applicant_id) as any));
    }
    setApps(apps);
    setMyApp(apps.find((x) => x.applicant_id === user?.id) ?? null);
    setProofs((pr as Proof[]) ?? []);
    setDispute((d as Dispute) ?? null);
    setReviews((rv as Review[]) ?? []);

    // Sign proof media urls
    const allMedia = ((pr as Proof[]) ?? []).flatMap((p) => p.media_urls ?? []);
    if (allMedia.length) {
      const { data: signed } = await supabase.storage.from("gig-evidence").createSignedUrls(allMedia, 3600);
      const map: Record<string, string> = {};
      (signed ?? []).forEach((s, i) => { if (s.signedUrl) map[allMedia[i]] = s.signedUrl; });
      setProofUrls(map);
    }
    setLoading(false);
  }, [id, user?.id]);

  useEffect(() => { load(); }, [load]);

  if (loading)
    return <MobileShell><BrandHeader /><div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></MobileShell>;
  if (!gig)
    return <MobileShell><BrandHeader /><div className="p-6 text-center text-muted-foreground">Gig not found.</div></MobileShell>;

  const isPoster = user?.id === gig.poster_id;
  const isWorker = user?.id === gig.worker_id;

  const apply = async () => {
    setBusy(true);
    const { error } = await supabase.rpc("apply_to_gig", { _gig_id: gig.id, _message: message || null });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Application sent"); setMessage(""); load();
  };
  const accept = async (appId: string) => {
    setBusy(true);
    const { error } = await supabase.rpc("accept_gig_application", { _application_id: appId });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Worker accepted"); load();
  };
  const approve = async () => {
    if (!/^\d{4}$/.test(pin)) return toast.error("Enter your PIN");
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("gig-complete", {
      body: { gig_id: gig.id, pin },
    });
    setBusy(false);
    if (error || (data as any)?.error) return toast.error((data as any)?.error ?? error?.message ?? "Failed");
    toast.success("Payment released — leave a review"); setPin(""); load();
  };

  const reject = async () => {
    if (disputeReason.trim().length < 10) return toast.error("Reason must be 10+ chars");
    setBusy(true);
    const { data, error } = await supabase.rpc("reject_gig_completion", { _gig_id: gig.id, _reason: disputeReason.trim(), _evidence_urls: [] });
    if (error) { setBusy(false); return toast.error(error.message); }
    // Trigger AI review
    if (data && (data as any).id) {
      supabase.functions.invoke("dispute-ai-review", { body: { dispute_id: (data as any).id } }).catch(() => {});
    }
    setBusy(false); toast.success("Dispute opened"); setShowDispute(false); setDisputeReason(""); load();
  };
  const openDispute = async () => {
    if (disputeReason.trim().length < 10) return toast.error("Reason must be 10+ chars");
    setBusy(true);
    const { data, error } = await supabase.rpc("open_dispute", { _gig_id: gig.id, _reason: disputeReason.trim(), _evidence_urls: [] });
    if (error) { setBusy(false); return toast.error(error.message); }
    if (data && (data as any).id) {
      supabase.functions.invoke("dispute-ai-review", { body: { dispute_id: (data as any).id } }).catch(() => {});
    }
    setBusy(false); toast.success("Dispute opened"); setShowDispute(false); setDisputeReason(""); load();
  };
  const cancel = async () => {
    if (!confirm("Cancel this gig and refund escrow?")) return;
    setBusy(true);
    const { error } = await supabase.rpc("cancel_gig", { _gig_id: gig.id });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Cancelled — refunded"); load();
  };

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)} right={<NotificationsBell />} />
      <div className="px-4 pt-2 pb-2 flex items-center gap-2">
        <span className="text-xs font-semibold uppercase text-muted-foreground">{gig.status.replace("_", " ")}</span>
        {dispute && <RiskBadge risk={dispute.ai_risk} />}
      </div>

      <div className="px-4 pb-24 space-y-4">
        <div className="rounded-2xl bg-card border border-border p-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h1 className="font-display font-extrabold text-xl leading-tight">{gig.title}</h1>
            <span className="font-display font-extrabold text-primary text-lg shrink-0">{formatMoney(gig.budget_cents)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[10px] font-semibold uppercase bg-muted px-2 py-0.5 rounded-full">{TAG_LABEL[gig.tag]}</span>
            {gig.type === "drop" && (
              <span className="text-[10px] font-bold uppercase bg-primary/15 text-primary px-2 py-0.5 rounded-full flex items-center gap-1">
                <Zap className="h-3 w-3" /> Drop
              </span>
            )}
            {gig.expires_at && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> expires {new Date(gig.expires_at).toLocaleString()}</span>
            )}
            {gig.location && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {gig.location}</span>
            )}
          </div>
          <p className="text-sm whitespace-pre-wrap">{gig.description}</p>
          {poster && (
            <p className="mt-3 text-xs text-muted-foreground">
              Posted by{" "}
              <button onClick={() => navigate(`/u/${poster.cashtag}`)} className="font-semibold text-foreground">${poster.cashtag}</button>
            </p>
          )}
        </div>

        {/* Dispute card */}
        {dispute && (
          <div className="rounded-2xl bg-destructive/5 border border-destructive/30 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-destructive" />
              <p className="text-sm font-semibold">Dispute — {dispute.status.replace(/_/g, " ")}</p>
            </div>
            <p className="text-xs text-muted-foreground">Opened by {dispute.opener_role}: "{dispute.reason}"</p>
            {dispute.ai_summary && (
              <div className="text-xs bg-muted/50 rounded-lg p-2">
                <p className="font-semibold mb-1">AI review:</p>
                <p>{dispute.ai_summary}</p>
              </div>
            )}
            {dispute.resolved_at && (
              <p className="text-xs">
                Resolved: worker {formatMoney(dispute.worker_payout_cents ?? 0)} · poster refund {formatMoney(dispute.poster_refund_cents ?? 0)}
              </p>
            )}
            <Link to="/disputes" className="text-xs text-primary font-medium">View all my disputes →</Link>
          </div>
        )}

        {/* Proofs */}
        {proofs.length > 0 && (
          <div className="rounded-2xl bg-card border border-border p-4 space-y-3">
            <p className="text-sm font-semibold flex items-center gap-2"><Camera className="h-4 w-4" /> Completion proof</p>
            {proofs.map((p) => (
              <div key={p.id} className="space-y-2">
                {p.note && <p className="text-sm">{p.note}</p>}
                {p.media_urls?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {p.media_urls.map((m) => (
                      <a key={m} href={proofUrls[m]} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden bg-muted grid place-items-center">
                        {proofUrls[m] ? <img src={proofUrls[m]} alt="proof" className="w-full h-full object-cover" /> : <ImageIcon className="h-5 w-5 text-muted-foreground" />}
                      </a>
                    ))}
                  </div>
                )}
                <div className="flex gap-3 text-[10px] text-muted-foreground">
                  <span>{new Date(p.submitted_at).toLocaleString()}</span>
                  {p.gps_lat != null && <span><MapPin className="h-3 w-3 inline" /> {p.gps_lat.toFixed(4)}, {p.gps_lng?.toFixed(4)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Worker: submit proof */}
        {isWorker && (gig.status === "in_progress" || gig.status === "pending_approval") && (
          <ProofUploader gigId={gig.id} onSubmitted={load} />
        )}

        {/* Poster: approve / reject when proof submitted */}
        {isPoster && (gig.status === "pending_approval" || gig.status === "in_progress") && (
          <div className="rounded-2xl bg-card border border-border p-4 space-y-3">
            <p className="text-sm font-semibold">{gig.status === "pending_approval" ? "Worker requested approval" : "Release payment"}</p>
            <Input type="password" inputMode="numeric" maxLength={4} placeholder="PIN" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} />
            <Button onClick={approve} disabled={busy} className="w-full rounded-full">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Approve & release {formatMoney(gig.budget_cents)}
            </Button>
            {gig.worker_id && (
              <Button variant="outline" onClick={() => setShowDispute((s) => !s)} disabled={busy} className="w-full rounded-full">
                <XCircle className="h-4 w-4 mr-2" /> Reject & open dispute
              </Button>
            )}
            {showDispute && (
              <div className="space-y-2 pt-2 border-t border-border">
                <Textarea value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} placeholder="Why are you rejecting? Be specific (10+ chars)" rows={3} maxLength={1000} />
                <Button variant="destructive" onClick={reject} disabled={busy} className="w-full rounded-full">Confirm dispute</Button>
              </div>
            )}
          </div>
        )}

        {/* Worker: open dispute (e.g. poster won't release) */}
        {isWorker && gig.status === "pending_approval" && !dispute && (
          <div className="rounded-2xl bg-card border border-border p-4 space-y-3">
            <p className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-accent" /> Poster not responding?</p>
            <Button variant="outline" onClick={() => setShowDispute((s) => !s)} disabled={busy} className="w-full rounded-full">Open dispute</Button>
            {showDispute && (
              <div className="space-y-2">
                <Textarea value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} placeholder="Explain the situation (10+ chars)" rows={3} maxLength={1000} />
                <Button variant="destructive" onClick={openDispute} disabled={busy} className="w-full rounded-full">Open dispute</Button>
              </div>
            )}
          </div>
        )}

        {/* Poster: cancel before assignment */}
        {isPoster && gig.status === "open" && (
          <Button variant="outline" onClick={cancel} disabled={busy} className="w-full rounded-full">
            <XCircle className="h-4 w-4 mr-2" /> Cancel & refund
          </Button>
        )}

        {/* Apply form */}
        {!isPoster && gig.status === "open" && !myApp && (
          <div className="rounded-2xl bg-card border border-border p-4 space-y-3">
            <p className="text-sm font-semibold">Apply for this gig</p>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Why you're a fit (optional)" maxLength={500} rows={3} />
            <Button onClick={apply} disabled={busy} className="w-full rounded-full">Send application</Button>
          </div>
        )}
        {!isPoster && myApp && (
          <div className="rounded-2xl bg-muted/40 border border-border p-3 text-sm">
            Your application: <span className="font-semibold">{myApp.status}</span>
          </div>
        )}

        {/* Poster: applications list */}
        {isPoster && (
          <div className="space-y-2">
            <h2 className="font-display font-bold text-sm">Applications ({apps.length})</h2>
            {apps.length === 0 && <p className="text-xs text-muted-foreground">No applicants yet.</p>}
            {apps.map((a) => (
              <div key={a.id} className="rounded-2xl bg-card border border-border p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-display font-bold text-sm">
                    {a.applicant?.display_name ?? "User"}{" "}
                    <span className="text-xs text-muted-foreground font-normal">${a.applicant?.cashtag} · {a.applicant?.hustle_level}</span>
                  </p>
                  <span className="text-[10px] uppercase font-semibold text-muted-foreground">{a.status}</span>
                </div>
                {a.message && <p className="text-sm text-muted-foreground mb-2">{a.message}</p>}
                {gig.status === "open" && a.status === "pending" && (
                  <Button size="sm" onClick={() => accept(a.id)} disabled={busy}>Accept</Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Reviews — visible once gig completed */}
        {gig.status === "completed" && gig.worker_id && (
          <div className="space-y-3">
            <h2 className="font-display font-bold text-sm">Reviews</h2>
            {reviews.map((r) => (
              <div key={r.id} className="rounded-2xl bg-card border border-border p-3">
                <div className="flex items-center gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`h-3.5 w-3.5 ${n <= r.stars ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                  ))}
                  <span className="ml-2 text-[10px] uppercase text-muted-foreground">
                    {r.direction === "employer_to_worker" ? "Employer → Worker" : "Worker → Employer"}
                  </span>
                </div>
                {r.comment && <p className="text-sm">{r.comment}</p>}
              </div>
            ))}
            {isPoster && !reviews.find((r) => r.reviewer_id === user?.id) && (
              <ReviewForm gigId={gig.id} subjectId={gig.worker_id} direction="employer_to_worker" onSubmitted={load} />
            )}
            {isWorker && !reviews.find((r) => r.reviewer_id === user?.id) && (
              <ReviewForm gigId={gig.id} subjectId={gig.poster_id} direction="worker_to_employer" onSubmitted={load} />
            )}
          </div>
        )}

        {/* Report this gig */}
        {user && !isPoster && (
          <div className="rounded-2xl bg-card border border-border p-4 space-y-2">
            <p className="text-sm font-semibold flex items-center gap-1">
              <ShieldAlert className="h-4 w-4" /> Report a problem
            </p>
            <Textarea
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="What's wrong with this listing? (scam, unsafe, fake, etc.)"
              rows={2}
              maxLength={500}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={busy || reportReason.trim().length < 5}
              onClick={async () => {
                if (!user || !gig) return;
                setBusy(true);
                const { error } = await supabase.from("gig_reports").insert({
                  reporter_id: user.id,
                  gig_id: gig.id,
                  reported_user: gig.poster_id,
                  reason: reportReason.trim().slice(0, 120),
                  details: reportReason.trim(),
                });
                setBusy(false);
                if (error) toast.error(error.message);
                else {
                  toast.success("Report submitted. Our team will review.");
                  setReportReason("");
                }
              }}
              className="rounded-full"
            >
              Submit report
            </Button>
          </div>
        )}
      </div>
    </MobileShell>
  );
};

export default GigDetail;
